import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/material";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import { CancelButton } from "../shared/CancelButton";
import { MaximumFileSizeBytes, WorkOrderResultUpload } from "../shared/interfaces/Result";
import { InternationalDatepicker } from "../shared/InternationalDatepicker";
import { DropzoneUploader } from "../shared/DropzoneUploader";
import { FormRow } from "../shared/FormLayout/FormRow";
import { FormContainer } from "../shared/FormLayout/FormContainer";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import { ProgressButton } from "../shared/ProgressButton";
import { useWorkOrderResultFileUpload } from "../data/Workorders/useWorkorderResultFileUpload";
import { CalendarChangeParams } from "primereact/calendar";

interface Props {
  workorderId: number;
  customerId: number;
  open: boolean;
  onUploadSuccess: () => void;
  onCancel: () => void;
}

export function CompleteWorkorderDialog(props: Props) {
  const { t } = useTranslation();
  const { workorderId, customerId, open, onUploadSuccess, onCancel } = props;

  const [resultUpload, setResultUpload] = useState<Partial<WorkOrderResultUpload>>({
    workOrderId: workorderId,
    customerId: customerId,
  });
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  const workorderResultFileUpload = useWorkOrderResultFileUpload({
    onSuccess: onUploadSuccess,
    onError: handleResultUploadError,
  });

  const handleValueChange = (field: keyof WorkOrderResultUpload) => {
    return function (e: CalendarChangeParams | React.ChangeEvent<HTMLTextAreaElement> | undefined) {
      setResultUpload((prevState) => ({
        ...prevState,
        [field]: e?.target?.value,
      }));
    };
  };

  const handleFileSelection = useCallback(async (file: File[]) => {
    if (file && file.length > 0) {
      setResultUpload((prevState) => ({ ...prevState, resultFile: file[0] }));
    }
  }, []);

  function submitResults() {
    workorderResultFileUpload.mutate(resultUpload as WorkOrderResultUpload);
  }

  function handleResultUploadError(error: Response) {
    if (error.status === 415) {
      setUploadErrorMessage(t("fileUpload.fileInvalidType"));
    } else {
      setUploadErrorMessage(t("fileUpload.uploadError"));
    }
  }

  return (
    <>
      <Dialog
        header={t("workorders.actions.completeWorkOrder")}
        footer={
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <MessageToast text={uploadErrorMessage} mode={ToastMode.Error} show={workorderResultFileUpload.isError} />
            <ProgressButton
              onClick={submitResults}
              disabled={!resultUpload?.resultFile || !resultUpload?.completionDate || !resultUpload?.startDate}
              loading={workorderResultFileUpload.isLoading}
              startIcon={<FontAwesomeIcon icon={faUpload} />}
            >
              {t("workorders.actions.completeWorkOrder")}
            </ProgressButton>
            <CancelButton onClick={onCancel} disabled={workorderResultFileUpload.isLoading} />
          </Stack>
        }
        visible={open}
        onHide={onCancel}
        breakpoints={{ "960px": "75vw", "1920px": "85vw" }}
        style={{ width: "65vw" }}
      >
        <FormContainer>
          <FormRow
            label={t("workorders.startDate")}
            value={
              <InternationalDatepicker
                maxDate={resultUpload?.completionDate}
                value={resultUpload?.startDate}
                onChange={handleValueChange("startDate")}
                showIcon
                showTime
              />
            }
          />
          <FormRow
            label={t("workorders.completionDate")}
            value={
              <InternationalDatepicker
                minDate={resultUpload?.startDate}
                value={resultUpload?.completionDate}
                onChange={handleValueChange("completionDate")}
                showIcon
                showTime
              />
            }
          />
          <FormRow
            label={t("workorders.notes")}
            value={
              <InputTextarea
                value={resultUpload?.notes}
                onChange={handleValueChange("notes")}
                autoResize
                style={{ width: "100%" }}
              />
            }
          />
          <FormRow
            label={t("results.resultFile")}
            value={
              <DropzoneUploader
                acceptedMimeTypes="application/zip, application/x-zip-compressed"
                acceptedFileTypeTranslationKeys={["fileTypes.zipExtension"]}
                maxFiles={1}
                maxSize={MaximumFileSizeBytes}
                onFileSelection={handleFileSelection}
              />
            }
          />
        </FormContainer>
      </Dialog>
    </>
  );
}
